import React, { useState } from 'react';
// Native MUI Components
import {
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Grid,
} from '@mui/material';
import { Trip } from '@/types/trip';
import * as tripService from '@/services/tripService';
import { useToast } from '@/hooks/use-toast';

interface DuplicateTripFormProps {
  trip: Trip;
  onTripDuplicated: (newTrip: Trip) => void;
  onCancel: () => void;
}

const DuplicateTripForm: React.FC<DuplicateTripFormProps> = ({ trip, onTripDuplicated, onCancel }) => {
  const [newTripName, setNewTripName] = useState(`${trip.name} (Copy)`);
  const [options, setOptions] = useState({
    includeApartments: true,
    includeTravellers: false,
    includeSkiPasses: true,
    includeApartmentTypes: true,
    includeTransportation: true,
  });
  const { toast } = useToast();

  const handleOptionChange = (option: keyof typeof options, value: boolean) => {
    setOptions((prev) => ({ ...prev, [option]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTripName) {
      toast({
        title: 'Error',
        description: 'Please enter a name for the new trip.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const newTrip = await tripService.duplicateTrip(trip.id, newTripName, options);
      toast({
        title: 'Success',
        description: 'Trip duplicated successfully.',
      });
      onTripDuplicated(newTrip);
    } catch (error) {
      console.error('Error duplicating trip:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate trip. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Box component="form" id="duplicate-trip-form" onSubmit={handleSubmit}>
      <Grid container spacing={3} sx={{ py: 2 }}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="New Name"
            value={newTripName}
            onChange={(e) => setNewTripName(e.target.value)}
            required
          />
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Include in new trip:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={options.includeApartments}
                  onChange={(e) => handleOptionChange('includeApartments', e.target.checked)}
                />
              }
              label="Apartments"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={options.includeTravellers}
                  onChange={(e) => handleOptionChange('includeTravellers', e.target.checked)}
                />
              }
              label="Travellers"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={options.includeSkiPasses}
                  onChange={(e) => handleOptionChange('includeSkiPasses', e.target.checked)}
                />
              }
              label="Ski Passes"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={options.includeApartmentTypes}
                  onChange={(e) => handleOptionChange('includeApartmentTypes', e.target.checked)}
                />
              }
              label="Apartment Types"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={options.includeTransportation}
                  onChange={(e) => handleOptionChange('includeTransportation', e.target.checked)}
                />
              }
              label="Transportation"
            />
          </Box>
        </Grid>
      </Grid>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" form="duplicate-trip-form" type="submit">
          Duplicate
        </Button>
      </Box>
    </Box>
  );
};

export default DuplicateTripForm;
